package fr.manyverse.app.presentation.viewmodel

import androidx.lifecycle.viewModelScope
import fr.manyverse.app.domain.image.ImageTarget
import fr.manyverse.app.domain.model.CharacterSheet
import fr.manyverse.app.domain.model.GmInventoryItem
import fr.manyverse.app.domain.model.ImageData
import fr.manyverse.app.domain.model.PreMadeCharacter
import fr.manyverse.app.domain.model.PreMadeCharacterToCreate
import fr.manyverse.app.domain.model.PreMadeInventoryItem
import fr.manyverse.app.domain.repository.ImageStorageRepository
import fr.manyverse.app.domain.usecase.CreatePreMadeCharacterUseCase
import fr.manyverse.app.domain.usecase.DeletePreMadeCharacterImageUseCase
import fr.manyverse.app.domain.usecase.DeletePreMadeCharacterUseCase
import fr.manyverse.app.domain.usecase.GetGmInventoryItemsUseCase
import fr.manyverse.app.domain.usecase.GetPreMadeCharactersUseCase
import fr.manyverse.app.domain.usecase.UpdatePreMadeCharacterUseCase
import fr.manyverse.app.domain.usecase.UploadPreMadeCharacterImageUseCase
import fr.manyverse.app.domain.usecase.UploadPreMadeInventoryItemImageUseCase
import fr.manyverse.app.presentation.components.premade.PreMadeItemDraft
import fr.manyverse.app.presentation.state.AsyncUiState
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.launch

class PreMadeCharactersViewModel(
    private val tableId: String,
    private val getPreMadeCharactersUseCase: GetPreMadeCharactersUseCase,
    private val createPreMadeCharacterUseCase: CreatePreMadeCharacterUseCase,
    private val updatePreMadeCharacterUseCase: UpdatePreMadeCharacterUseCase,
    private val deletePreMadeCharacterUseCase: DeletePreMadeCharacterUseCase,
    private val uploadPreMadeCharacterImageUseCase: UploadPreMadeCharacterImageUseCase,
    private val deletePreMadeCharacterImageUseCase: DeletePreMadeCharacterImageUseCase,
    private val uploadPreMadeInventoryItemImageUseCase: UploadPreMadeInventoryItemImageUseCase,
    private val getGmInventoryItemsUseCase: GetGmInventoryItemsUseCase,
    private val imageStorageRepository: ImageStorageRepository
) : BaseViewModel() {

    private val _listState =
        MutableStateFlow<AsyncUiState<List<PreMadeCharacter>>>(AsyncUiState.Loading)
    val listState: StateFlow<AsyncUiState<List<PreMadeCharacter>>> = _listState.asStateFlow()

    private val _createState = MutableStateFlow<AsyncUiState<Unit>>(AsyncUiState.Idle)
    val createState: StateFlow<AsyncUiState<Unit>> = _createState.asStateFlow()

    private val _updateState = MutableStateFlow<AsyncUiState<Unit>>(AsyncUiState.Idle)
    val updateState: StateFlow<AsyncUiState<Unit>> = _updateState.asStateFlow()

    private val _deleteState = MutableStateFlow<AsyncUiState<Unit>>(AsyncUiState.Idle)
    val deleteState: StateFlow<AsyncUiState<Unit>> = _deleteState.asStateFlow()

    // Objets du coffre MJ, pour la popup d'import d'objets vers un pré-tiré.
    private val _gmChestItems = MutableStateFlow<List<GmInventoryItem>>(emptyList())
    val gmChestItems: StateFlow<List<GmInventoryItem>> = _gmChestItems.asStateFlow()

    init {
        load()
        loadGmChestItems()
    }

    fun load() {
        launchAsync(_listState) {
            getPreMadeCharactersUseCase(tableId).map { items ->
                items.sortedByDescending { it.createdAt }
            }
        }
    }

    private fun loadGmChestItems() {
        viewModelScope.launch {
            getGmInventoryItemsUseCase(tableId).onSuccess { _gmChestItems.value = it }
        }
    }

    fun create(
        name: String,
        story: String,
        imageData: ImageData? = null,
        sheet: CharacterSheet = CharacterSheet(),
        itemDrafts: List<PreMadeItemDraft> = emptyList()
    ) {
        launchAsyncWithCallback(_createState, {
            val inventory = resolveDrafts(itemDrafts).getOrElse {
                return@launchAsyncWithCallback Result.failure(it)
            }

            val createResult = createPreMadeCharacterUseCase(
                PreMadeCharacterToCreate(
                    tableId = tableId,
                    name = name,
                    story = story,
                    sheet = sheet,
                    inventory = inventory
                )
            )
            if (createResult.isFailure) return@launchAsyncWithCallback createResult.map { }

            val created = createResult.getOrThrow()

            if (imageData != null) {
                val uploadResult = uploadPreMadeCharacterImageUseCase(tableId, created.id, imageData)
                if (uploadResult.isSuccess) {
                    updatePreMadeCharacterUseCase(
                        id = created.id,
                        name = name,
                        story = story,
                        imageUrl = uploadResult.getOrThrow(),
                        sheet = sheet,
                        inventory = inventory
                    )
                }
            }

            Result.success(Unit)
        }) {
            load()
        }
    }

    fun update(
        id: String,
        name: String,
        story: String,
        imageData: ImageData? = null,
        removeImage: Boolean = false,
        currentImageUrl: String? = null,
        sheet: CharacterSheet = CharacterSheet(),
        itemDrafts: List<PreMadeItemDraft> = emptyList(),
        previousInventory: List<PreMadeInventoryItem> = emptyList()
    ) {
        launchAsyncWithCallback(_updateState, {
            val inventory = resolveDrafts(itemDrafts).getOrElse {
                return@launchAsyncWithCallback Result.failure(it)
            }

            // Supprime les images des objets retirés de l'inventaire.
            val keptIds = inventory.map { it.id }.toSet()
            previousInventory
                .filter { it.id !in keptIds && it.imageUrl != null }
                .forEach { removed ->
                    imageStorageRepository.delete(ImageTarget.PreMadeInventoryItem(tableId, removed.id))
                }

            var finalImageUrl: String? = currentImageUrl

            if (removeImage && currentImageUrl != null) {
                deletePreMadeCharacterImageUseCase(tableId, id)
                finalImageUrl = null
            }

            if (imageData != null) {
                val uploadResult = uploadPreMadeCharacterImageUseCase(tableId, id, imageData)
                if (uploadResult.isSuccess) {
                    finalImageUrl = uploadResult.getOrThrow()
                }
            }

            updatePreMadeCharacterUseCase(id, name, story, finalImageUrl, sheet, inventory).map { }
        }) {
            load()
        }
    }

    /**
     * Résout chaque brouillon d'objet en [PreMadeInventoryItem] persistable, en uploadant/copiant
     * les images nécessaires. Un échec d'upload d'image choisie localement est bloquant ; une copie
     * depuis le coffre qui échoue est non bloquante (objet conservé sans image).
     */
    private suspend fun resolveDrafts(drafts: List<PreMadeItemDraft>): Result<List<PreMadeInventoryItem>> {
        val resolved = mutableListOf<PreMadeInventoryItem>()
        for (draft in drafts) {
            val imageUrl: String? = when {
                draft.newImage != null -> {
                    uploadPreMadeInventoryItemImageUseCase(tableId, draft.id, draft.newImage)
                        .getOrElse { return Result.failure(it) }
                }

                draft.copyFromPath != null -> {
                    val downloaded = imageStorageRepository.downloadImage(draft.copyFromPath).getOrNull()
                    downloaded?.let {
                        imageStorageRepository
                            .upload(ImageTarget.PreMadeInventoryItem(tableId, draft.id), it)
                            .getOrNull()
                    }
                }

                draft.removeImage -> {
                    if (draft.existingImageUrl != null) {
                        imageStorageRepository.delete(ImageTarget.PreMadeInventoryItem(tableId, draft.id))
                    }
                    null
                }

                else -> draft.existingImageUrl
            }
            resolved.add(
                PreMadeInventoryItem(
                    id = draft.id,
                    name = draft.name,
                    category = draft.category,
                    quantity = draft.quantity,
                    imageUrl = imageUrl
                )
            )
        }
        return Result.success(resolved)
    }

    fun delete(id: String) {
        launchAsyncWithCallback(_deleteState, {
            deletePreMadeCharacterUseCase(id)
        }) {
            load()
        }
    }

    suspend fun getSignedImageUrl(imagePath: String): Result<String> {
        return imageStorageRepository.getSignedUrl(imagePath)
    }

    fun resetCreateState() = resetState(_createState)
    fun resetUpdateState() = resetState(_updateState)
    fun resetDeleteState() = resetState(_deleteState)
}
